import { asString } from "@/components/shapes/shared/plain-record";
import type { WidgetFaceProps } from "@/components/shapes/shared/widget-face";
import {
  WIDGET_CAPTION,
  WIDGET_DISPLAY,
  WIDGET_MUTED,
  WidgetDot,
  WidgetEmpty,
  WidgetFaceRoot,
  WidgetMore,
  WidgetRule,
} from "@/components/shapes/shared/widget-face-parts";
import { fitRows } from "@/components/shapes/shared/widget-rows";
import { useT } from "@/i18n";
import { framesAgendaMessages } from "@/i18n/messages/frames-agenda";
import { cn } from "@/lib/cn";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import { previewSource } from "@/lib/frame-preview-source";
import { dateParts, toAgendaItems, type AgendaItem } from "./agenda-items";

const ROWS = { medium: 4, large: 7 } as const;

/** The date's weekday, in the agenda's date colour. */
const WEEKDAY = cn(WIDGET_CAPTION, "text-[color:var(--gadget-date)]");

const NOW_DOT = "var(--color-accent)";

/** Time and title per row, the current one in bold with a dot. */
function AgendaRows({
  rows,
  large,
}: {
  rows: readonly AgendaItem[];
  large: boolean;
}) {
  const t = useT(framesAgendaMessages);
  return (
    <div
      className={cn(
        "grid min-w-0 grid-cols-[42px_1fr] items-baseline gap-x-2",
        large ? "gap-y-[14px]" : "content-center gap-y-[9px]",
      )}
    >
      {rows.map((row) => [
        <span
          key={`${row.id}-time`}
          className={cn("tabular-nums", WIDGET_MUTED)}
        >
          {row.time}
        </span>,
        <span
          key={row.id}
          className={cn(
            "flex min-w-0 items-center gap-1.5",
            row.current && "font-semibold",
          )}
          data-testid={`agenda-widget-row-${row.id}`}
        >
          {row.current && <WidgetDot color={NOW_DOT} />}
          <span className="truncate">{row.title}</span>
          {large && row.current && (
            <span className={cn("shrink-0 font-normal", WIDGET_MUTED)}>
              · {t("now")}
            </span>
          )}
        </span>,
      ])}
    </div>
  );
}

/**
 * A day's agenda on the desktop. Small: the current (or first) item and
 * the next. Medium: the date beside the rows. Large: the date over every
 * row that fits, then when the day ends.
 */
export function AgendaWidgetFace({ frame, size }: WidgetFaceProps) {
  const t = useT(framesAgendaMessages);
  const source = previewSource(frame);
  const items = toAgendaItems(source.items) ?? [];
  const first = items[0];
  if (!first) {
    return (
      <WidgetEmpty frame={frame} size={size} message={t("nothingScheduled")} />
    );
  }
  const date = asString(source.date) || null;
  const { day, weekday } = dateParts(date);
  const title = frameDisplayTitle(frame);

  if (size === "small") {
    const focusIndex = Math.max(
      0,
      items.findIndex((item) => item.current),
    );
    const focus = items[focusIndex] ?? first;
    const next = items[focusIndex + 1];
    return (
      <WidgetFaceRoot frame={frame} size={size}>
        <p className={cn("truncate", WIDGET_CAPTION, WIDGET_MUTED)}>
          {(day ? [weekday, day].filter(Boolean).join(" ") : date) ?? title}
        </p>
        {focus.current ? (
          <p className="flex items-center gap-1.5 font-semibold">
            <WidgetDot color={NOW_DOT} />
            <span className="truncate">
              {[t("now"), focus.time].filter(Boolean).join(" · ")}
            </span>
          </p>
        ) : (
          focus.time && <p className={WIDGET_MUTED}>{focus.time}</p>
        )}
        <p
          className={cn("line-clamp-2", WIDGET_DISPLAY.m)}
          data-testid="agenda-widget-focus"
        >
          {focus.title}
        </p>
        {next && (
          <p
            className={cn("mt-auto truncate", WIDGET_MUTED)}
            data-testid="agenda-widget-next"
          >
            {[t("next"), next.time, next.title].filter(Boolean).join(" ")}
          </p>
        )}
      </WidgetFaceRoot>
    );
  }

  const { shown, more } = fitRows(items, ROWS[size]);
  const last = items[items.length - 1] ?? first;

  if (size === "medium") {
    return (
      <WidgetFaceRoot
        frame={frame}
        size={size}
        className="grid grid-cols-[96px_1fr] gap-4"
      >
        <div className="flex min-w-0 flex-col">
          {weekday && <p className={WEEKDAY}>{weekday}</p>}
          <p
            className={cn(
              day ? WIDGET_DISPLAY.xl : cn("line-clamp-3", WIDGET_DISPLAY.m),
            )}
          >
            {day ?? date ?? title}
          </p>
          {(day ?? date) && (
            <p className={cn("mt-auto line-clamp-2", WIDGET_MUTED)}>{title}</p>
          )}
        </div>
        <div className="grid min-w-0 content-center gap-[9px]">
          <AgendaRows rows={shown} large={false} />
          <WidgetMore count={more} />
        </div>
      </WidgetFaceRoot>
    );
  }

  return (
    <WidgetFaceRoot frame={frame} size={size}>
      <div className="flex min-w-0 items-baseline gap-2.5">
        {day && <p className={WIDGET_DISPLAY.l}>{day}</p>}
        <div className="min-w-0">
          {weekday && <p className={WEEKDAY}>{weekday}</p>}
          <p className={cn("truncate", WIDGET_MUTED)}>
            {day ? title : (date ?? title)}
          </p>
        </div>
      </div>
      <WidgetRule className="mb-1 mt-1.5" />
      <AgendaRows rows={shown} large />
      <WidgetMore count={more} />
      {more === 0 && last.time && (
        <p className={cn("mt-auto truncate", WIDGET_MUTED)}>
          {t("nothingAfter", { time: last.time })}
        </p>
      )}
    </WidgetFaceRoot>
  );
}
